import { DateTime } from 'luxon';
import {
  IPlatformLead,
  IPlatformReminder,
  DEFAULT_VISIBLE_STAGES,
  HIDDEN_STAGES,
  OPEN_STAGES,
  PlatformLeadActivityTypeEnum,
  PlatformLeadOutcomeEnum,
  PlatformLeadPriorityEnum,
  PlatformLeadSourceEnum,
  PlatformLeadStageEnum,
} from '@torim-app/types';

export const STALE_DAYS = 7;

export { DEFAULT_VISIBLE_STAGES, HIDDEN_STAGES, OPEN_STAGES };

export const STAGE_LABELS: Record<PlatformLeadStageEnum, string> = {
  [PlatformLeadStageEnum.New]: 'חדש',
  [PlatformLeadStageEnum.InProgress]: 'בטיפול',
  [PlatformLeadStageEnum.OnHold]: 'בהמתנה',
  [PlatformLeadStageEnum.Done]: 'טופל',
  [PlatformLeadStageEnum.Converted]: 'לקוח',
  [PlatformLeadStageEnum.Cold]: 'קר',
  [PlatformLeadStageEnum.Spam]: 'ספאם',
};

export const STAGE_COLORS: Record<PlatformLeadStageEnum, string> = {
  [PlatformLeadStageEnum.New]: '#3b82f6',
  [PlatformLeadStageEnum.InProgress]: '#8b5cf6',
  [PlatformLeadStageEnum.OnHold]: '#f59e0b',
  [PlatformLeadStageEnum.Done]: '#10b981',
  [PlatformLeadStageEnum.Converted]: '#059669',
  [PlatformLeadStageEnum.Cold]: '#94a3b8',
  [PlatformLeadStageEnum.Spam]: '#ef4444',
};

export const STAGE_SEVERITY: Record<PlatformLeadStageEnum, string> = {
  [PlatformLeadStageEnum.New]: 'info',
  [PlatformLeadStageEnum.InProgress]: 'secondary',
  [PlatformLeadStageEnum.OnHold]: 'warn',
  [PlatformLeadStageEnum.Done]: 'success',
  [PlatformLeadStageEnum.Converted]: 'success',
  [PlatformLeadStageEnum.Cold]: 'contrast',
  [PlatformLeadStageEnum.Spam]: 'danger',
};

export const STAGE_ICONS: Record<PlatformLeadStageEnum, string> = {
  [PlatformLeadStageEnum.New]: 'pi-sparkles',
  [PlatformLeadStageEnum.InProgress]: 'pi-spinner',
  [PlatformLeadStageEnum.OnHold]: 'pi-pause',
  [PlatformLeadStageEnum.Done]: 'pi-check',
  [PlatformLeadStageEnum.Converted]: 'pi-verified',
  [PlatformLeadStageEnum.Cold]: 'pi-times-circle',
  [PlatformLeadStageEnum.Spam]: 'pi-ban',
};

/** Chip styling for a status, used by the filter row and the lead cards */
export const STAGE_CHIP_CLASSES: Record<PlatformLeadStageEnum, string> = {
  [PlatformLeadStageEnum.New]: 'bg-blue-50 text-blue-700 border-blue-200',
  [PlatformLeadStageEnum.InProgress]: 'bg-purple-50 text-purple-700 border-purple-200',
  [PlatformLeadStageEnum.OnHold]: 'bg-amber-50 text-amber-700 border-amber-200',
  [PlatformLeadStageEnum.Done]: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  [PlatformLeadStageEnum.Converted]: 'bg-emerald-600 text-white border-emerald-600',
  [PlatformLeadStageEnum.Cold]: 'bg-slate-100 text-slate-600 border-slate-200',
  [PlatformLeadStageEnum.Spam]: 'bg-red-50 text-red-700 border-red-200',
};

export const STAGE_OPTIONS = Object.values(PlatformLeadStageEnum).map((value) => ({
  value,
  label: STAGE_LABELS[value],
}));

export const OUTCOME_LABELS: Record<PlatformLeadOutcomeEnum, string> = {
  [PlatformLeadOutcomeEnum.Answered]: 'ענה',
  [PlatformLeadOutcomeEnum.NoAnswer]: 'לא ענה',
  [PlatformLeadOutcomeEnum.WrongNumber]: 'מספר שגוי',
  [PlatformLeadOutcomeEnum.CallbackLater]: 'לחזור אליו',
};

export const OUTCOME_ICONS: Record<PlatformLeadOutcomeEnum, string> = {
  [PlatformLeadOutcomeEnum.Answered]: 'pi-check-circle',
  [PlatformLeadOutcomeEnum.NoAnswer]: 'pi-phone',
  [PlatformLeadOutcomeEnum.WrongNumber]: 'pi-exclamation-circle',
  [PlatformLeadOutcomeEnum.CallbackLater]: 'pi-clock',
};

export const OUTCOME_CHIP_CLASSES: Record<PlatformLeadOutcomeEnum, string> = {
  [PlatformLeadOutcomeEnum.Answered]: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  [PlatformLeadOutcomeEnum.NoAnswer]: 'bg-slate-100 text-slate-600 border-slate-200',
  [PlatformLeadOutcomeEnum.WrongNumber]: 'bg-red-50 text-red-700 border-red-200',
  [PlatformLeadOutcomeEnum.CallbackLater]: 'bg-amber-50 text-amber-700 border-amber-200',
};

export const SOURCE_LABELS: Record<PlatformLeadSourceEnum, string> = {
  [PlatformLeadSourceEnum.Signup]: 'הרשמה',
  [PlatformLeadSourceEnum.Manual]: 'ידני',
  [PlatformLeadSourceEnum.Referral]: 'הפניה',
  [PlatformLeadSourceEnum.Marketing]: 'שיווק',
  [PlatformLeadSourceEnum.Cold]: 'פנייה קרה',
  [PlatformLeadSourceEnum.Inbound]: 'פנייה נכנסת',
  [PlatformLeadSourceEnum.WhatsApp]: 'וואטסאפ',
  [PlatformLeadSourceEnum.Event]: 'אירוע',
};

export const SOURCE_ICONS: Record<PlatformLeadSourceEnum, string> = {
  [PlatformLeadSourceEnum.Signup]: 'pi-user-plus',
  [PlatformLeadSourceEnum.Manual]: 'pi-pencil',
  [PlatformLeadSourceEnum.Referral]: 'pi-share-alt',
  [PlatformLeadSourceEnum.Marketing]: 'pi-megaphone',
  [PlatformLeadSourceEnum.Cold]: 'pi-snowflake',
  [PlatformLeadSourceEnum.Inbound]: 'pi-inbox',
  [PlatformLeadSourceEnum.WhatsApp]: 'pi-whatsapp',
  [PlatformLeadSourceEnum.Event]: 'pi-flag',
};

/**
 * The two partners who work the pipeline. `value` is what gets stored on the
 * lead's `assignedTo`; add a third entry here and it shows up everywhere.
 */
export const ASSIGNEES: Array<{ value: string; label: string }> = [
  { value: 'omri', label: 'עומרי' },
  { value: 'peleg', label: 'פלג' },
];

/** Display name for a stored assignee, tolerating legacy email values */
export const assigneeLabel = (value?: string | null): string => {
  if (!value) return '';
  return ASSIGNEES.find((a) => a.value === value)?.label || value.split('@')[0];
};

export const PRIORITY_LABELS: Record<PlatformLeadPriorityEnum, string> = {
  [PlatformLeadPriorityEnum.Low]: 'נמוכה',
  [PlatformLeadPriorityEnum.Medium]: 'בינונית',
  [PlatformLeadPriorityEnum.High]: 'גבוהה',
};

export const PRIORITY_COLORS: Record<PlatformLeadPriorityEnum, string> = {
  [PlatformLeadPriorityEnum.Low]: '#cbd5e1',
  [PlatformLeadPriorityEnum.Medium]: '#3b82f6',
  [PlatformLeadPriorityEnum.High]: '#ef4444',
};

export const ACTIVITY_TYPE_LABELS: Record<PlatformLeadActivityTypeEnum, string> = {
  [PlatformLeadActivityTypeEnum.Note]: 'הערה',
  [PlatformLeadActivityTypeEnum.Call]: 'שיחה',
  [PlatformLeadActivityTypeEnum.Whatsapp]: 'וואטסאפ',
  [PlatformLeadActivityTypeEnum.Email]: 'אימייל',
  [PlatformLeadActivityTypeEnum.Meeting]: 'פגישה',
  [PlatformLeadActivityTypeEnum.System]: 'מערכת',
};

export const ACTIVITY_TYPE_ICONS: Record<PlatformLeadActivityTypeEnum, string> = {
  [PlatformLeadActivityTypeEnum.Note]: 'pi-comment',
  [PlatformLeadActivityTypeEnum.Call]: 'pi-phone',
  [PlatformLeadActivityTypeEnum.Whatsapp]: 'pi-whatsapp',
  [PlatformLeadActivityTypeEnum.Email]: 'pi-envelope',
  [PlatformLeadActivityTypeEnum.Meeting]: 'pi-calendar',
  [PlatformLeadActivityTypeEnum.System]: 'pi-cog',
};

export const ACTIVITY_TYPE_COLORS: Record<PlatformLeadActivityTypeEnum, string> = {
  [PlatformLeadActivityTypeEnum.Note]: 'bg-gray-100 text-gray-600',
  [PlatformLeadActivityTypeEnum.Call]: 'bg-blue-100 text-blue-700',
  [PlatformLeadActivityTypeEnum.Whatsapp]: 'bg-green-100 text-green-700',
  [PlatformLeadActivityTypeEnum.Email]: 'bg-purple-100 text-purple-700',
  [PlatformLeadActivityTypeEnum.Meeting]: 'bg-amber-100 text-amber-700',
  [PlatformLeadActivityTypeEnum.System]: 'bg-gray-50 text-gray-400',
};

export const isLeadStale = (lead: IPlatformLead): boolean => {
  if (!OPEN_STAGES.includes(lead.stage)) return false;
  if (!lead.lastContactedAt) return true;
  const cutoff = DateTime.local().minus({ days: STALE_DAYS });
  return DateTime.fromJSDate(new Date(lead.lastContactedAt)) < cutoff;
};

export const daysSinceContact = (lead: IPlatformLead): number | null => {
  if (!lead.lastContactedAt) return null;
  return Math.floor(
    DateTime.local().diff(DateTime.fromJSDate(new Date(lead.lastContactedAt)), 'days').days,
  );
};

/** "לפני 3 ימים" / "היום" — short enough to sit on a phone-width card */
export const relativeDate = (date?: Date | string | null): string => {
  if (!date) return 'מעולם לא';
  const dt = DateTime.fromJSDate(new Date(date)).startOf('day');
  const days = Math.round(DateTime.local().startOf('day').diff(dt, 'days').days);
  if (days === 0) return 'היום';
  if (days === 1) return 'אתמול';
  if (days === -1) return 'מחר';
  if (days < 0) return `בעוד ${Math.abs(days)} ימים`;
  if (days < 30) return `לפני ${days} ימים`;
  const months = Math.round(days / 30);
  if (months < 12) return `לפני ${months} חודשים`;
  return `לפני ${Math.round(months / 12)} שנים`;
};

export const formatDate = (date?: Date | string | null): string =>
  date ? DateTime.fromJSDate(new Date(date)).toFormat('dd/MM/yyyy') : '—';

export const formatDateTime = (date?: Date | string | null): string =>
  date ? DateTime.fromJSDate(new Date(date)).toFormat('dd/MM HH:mm') : '';

/** Hebrew label for a business contract status (trial / active / expired) */
export const contractStatusLabel = (status?: string): string => {
  const map: Record<string, string> = {
    trial: 'בניסיון',
    active: 'פעיל',
    expired: 'פג תוקף',
  };
  return status ? map[status] || status : '—';
};

export const formatCurrency = (n?: number): string => {
  if (!n || isNaN(n)) return '0 ₪';
  return new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency: 'ILS',
    maximumFractionDigits: 0,
  }).format(n);
};

export const isTaskOverdue = (task: IPlatformReminder): boolean =>
  !task.completed && new Date(task.dueDate).getTime() < Date.now();

export const isTaskToday = (task: IPlatformReminder): boolean => {
  const due = DateTime.fromJSDate(new Date(task.dueDate));
  return due.hasSame(DateTime.local(), 'day');
};

/** Normalises an Israeli number for tel:/wa.me links */
export const toDialable = (phone?: string): string => (phone || '').replace(/[^\d+]/g, '');

export const toWhatsappNumber = (phone?: string): string => {
  const digits = (phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('972')) return digits;
  if (digits.startsWith('0')) return `972${digits.slice(1)}`;
  return digits;
};
